import './OnboardingHint.scss';
import React, { ReactElement, ReactNode } from 'react';
import { Popover, type PopoverProps } from '@mantine/core';
import { MdClose } from 'react-icons/md';
import { usePortalTarget } from '../../provider/UiContext';

export interface IOnboardingHintBubbleProps {
    title: string;
    text: string;
    icon: ReactNode;
    /** Клик по телу подсказки — действие, к которому она ведёт (открыть редактор фото, меню) */
    onActivate?: () => void;
    /** Закрыть подсказку крестиком (и не показывать следующий шаг). Без обработчика крестика нет */
    onClose?: () => void;
}

/**
 * Содержимое онбординг-подсказки: акцентный бейдж с иконкой, заголовок и текст. Спокойная палитра приложения (один
 * акцент), в отличие от цветных подсказок ИИ.
 */
export const OnboardingHintBubble: React.FC<IOnboardingHintBubbleProps> = ({
    title,
    text,
    icon,
    onActivate,
    onClose,
}) => {
    const body = (
        <>
            <span className="avatar-hint__badge" aria-hidden="true">
                {icon}
            </span>
            <div className="avatar-hint__body">
                <div className="avatar-hint__title">{title}</div>
                <div className="avatar-hint__text">{text}</div>
            </div>
        </>
    );

    return (
        <div className="avatar-hint">
            {onActivate ? (
                <button type="button" className="avatar-hint__main" onClick={onActivate}>
                    {body}
                </button>
            ) : (
                <div className="avatar-hint__main">{body}</div>
            )}
            {onClose && (
                <button
                    type="button"
                    className="avatar-hint__close"
                    aria-label="Закрыть подсказку"
                    onClick={(e) => {
                        e.stopPropagation();
                        onClose();
                    }}
                >
                    <MdClose size={15} />
                </button>
            )}
        </div>
    );
};

export interface IOnboardingHintProps extends IOnboardingHintBubbleProps {
    opened: boolean;
    /** Цель подсказки: элемент, принимающий ref */
    children: ReactElement;
    position?: PopoverProps['position'];
    arrowPosition?: PopoverProps['arrowPosition'];
    /** @default 12 */
    offset?: number;
}

const HINT_TRANSITION: PopoverProps['transitionProps'] = {
    transition: 'pop',
    duration: 220,
    timingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)',
};

/** Онбординг-подсказка со стрелкой у цели (аватар, переключатель темы, кнопка меню пользователя). */
const OnboardingHint: React.FC<IOnboardingHintProps> = ({
    opened,
    children,
    position = 'left',
    arrowPosition,
    offset = 12,
    ...bubble
}) => {
    const portalTarget = usePortalTarget();

    return (
        <Popover
            opened={opened}
            position={position}
            arrowPosition={arrowPosition}
            withArrow
            arrowSize={12}
            arrowRadius={3}
            offset={offset}
            shadow="none"
            radius="md"
            classNames={{ dropdown: 'avatar-hint-dropdown', arrow: 'avatar-hint-arrow' }}
            transitionProps={HINT_TRANSITION}
            portalProps={portalTarget ? { target: portalTarget } : undefined}
        >
            <Popover.Target>{children}</Popover.Target>
            <Popover.Dropdown>
                <OnboardingHintBubble {...bubble} />
            </Popover.Dropdown>
        </Popover>
    );
};

export default OnboardingHint;
